<template>
  <div>
    <app-manage-grid
      :groups="tileGroups"
      :loading="fullscreenLoading"
      :title="`${meetingName}（${meetingCode}）`"
      @back="goBack"
    />
    <generate-wx-vue ref="generateWxRef" />
    <export-register ref="exportRegisterRef" />
    <hold-meet-user-manage ref="holdMeetUserManageRef" :meeting-type="'EXHIBITION'" />
  </div>
</template>

<script>
  import generateWxVue from '@/components/generateWx/generateWx.vue'
  import exportRegister from './components/exportRegister.vue'
  import holdMeetUserManage from './components/holdMeetUserManage.vue'
  import appManageGrid from './components/appManageGrid.vue'
  export default {
    name: 'AppManage',
    components: { generateWxVue, exportRegister, holdMeetUserManage, appManageGrid },
  }
</script>

<script setup>
  import { computed, getCurrentInstance, ref } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import {
    exportExOrderReport,
    exportExTicketReport,
    exportMeetingInvitedReport,
    exportIssueForum,
    exhibitionQueryPermissions,
  } from '@/api/exhibitionManage'
  import { downloadFileByUrl } from '@/utils/download'
  import { hasPermission } from '@/utils/permission'

  const { proxy } = getCurrentInstance()
  const route = useRoute()
  const router = useRouter()

  const meetingCode = route.query.meetingCode
  const aiApplicationType = route.query.aiApplicationType
  const meetingName = route.query.meetingName
  const pageId = route.query.pageId
  const logo = route.query.logo
  const enterpriseCode = route.query.enterpriseCode

  const isExhibition = aiApplicationType === 'EXHIBITION'
  const isJBH = computed(() => JSON.parse(localStorage.getItem('customInfo') || '{}')?.tenantKey === 'J80A84ZBBA40PW1')

  const generateWxRef = ref(null)
  const exportRegisterRef = ref(null)
  const holdMeetUserManageRef = ref(null)
  const fullscreenLoading = ref(false)

  const goBack = () => router.go(-1)

  // 重建行对象，供迁入的 handler 使用
  const rowObj = () => ({
    code: meetingCode,
    meetingCode,
    meetingName,
    pageId,
    logo,
    enterpriseCode,
    aiApplicationType,
    relationType: 'EXHIBITION',
  })

  const push = (path, query) => router.push({ path, query })

  // ---- 迁入的 handler（逻辑与原 exhibitionManage.vue 一致）----
  const getPermissionCustomized = async (row) => {
    const { data } = await exhibitionQueryPermissions({
      meetingCode: row.code,
      meetingType: 'EXHIBITION',
    })
    return data
  }

  const downLoadReport = async (type) => {
    const row = rowObj()
    const flag = await getPermissionCustomized(row)
    if (type === 'media' || type === 'signUp') {
      if (flag === 'HOST') {
        row.relationType = 'EXHIBITION'
        exportRegisterRef.value.handleOpen(
          row,
          type === 'media' ? `注册报表导出` : `签到报表导出`,
          type === 'media' ? `MEETING_REGISTER_REPORT` : `MEETING_SIGN_REPORT`
        )
      } else {
        proxy.$baseMessage('您没有访问该功能的权限', 'error', 'vab-hey-message-error')
      }
      return false
    }
    if (type === 'order') {
      if (flag === 'HOST') {
        fullscreenLoading.value = true
        exportExOrderReport(row.code).then((res) => {
          fullscreenLoading.value = false
          if (res.data.success) window.open(res.data.fileUrl)
        })
      } else {
        proxy.$baseMessage('您没有访问该功能的权限', 'error', 'vab-hey-message-error')
      }
      return false
    }
    if (type === 'ticket') {
      fullscreenLoading.value = true
      exportExTicketReport(row.code).then((res) => {
        fullscreenLoading.value = false
        if (res.data.success) window.open(res.data.fileUrl)
      })
      return false
    }
    if (type === 'business') {
      if (flag === 'HOST') {
        fullscreenLoading.value = true
        exportMeetingInvitedReport(row.code).then((res) => {
          fullscreenLoading.value = false
          if (res.data.success) window.open(res.data.fileUrl)
        })
      } else {
        proxy.$baseMessage('您没有访问该功能的权限', 'error', 'vab-hey-message-error')
      }
    }
  }

  const openPreview = () => {
    const row = rowObj()
    const qrCodeParams = {
      autoColor: false,
      b: 0,
      env: '',
      g: 0,
      jumpPath: row.code,
      jumpType: '',
      logo: row.logo,
      params: `{"navTitle":"${row.meetingName}", pageId: "${row.pageId}", exhibitionCode: "${row.code}"}`,
      path: 'pages/index/index',
      r: 0,
      scenesType: 'USER_HOLD_EXHIBITION',
      width: 1280,
      miniPath: `subPackage/shops/pages/index-page/index?navTitle=${row.meetingName}&pageId=${row.pageId}&meetingCode=${row.code}`,
    }
    generateWxRef.value.handleOpen(row, qrCodeParams)
  }

  const issueForumExportApi = () => {
    exportIssueForum(meetingCode).then(({ data: { fileUrl, fileName } }) => downloadFileByUrl(fileUrl, fileName))
  }

  // ---- tile 配置（按权限/类型预过滤显隐）----
  const checkGate = (gate) => {
    if (!gate) return true
    if (gate === 'exhibition') return isExhibition
    if (gate === '!jbh') return !isJBH.value
    if (gate === 'exhibition&!jbh') return isExhibition && !isJBH.value
    return true
  }
  const visible = (item) => (!item.perm || hasPermission(item.perm)) && checkGate(item.gate)

  const allGroups = [
    {
      name: '数据',
      items: [
        {
          label: '报名结果订阅',
          icon: 'Message',
          perm: ['user:admin:attend:audit'],
          action: () =>
            push('/exhibitionManage/meetingSubscription', { meetingCode, meetingName, meetingType: 'EXHIBITION' }),
        },
        {
          label: '数据统计',
          icon: 'DataAnalysis',
          perm: ['user:admin:event:report:meeting:statistics'],
          action: () => push('/exhData', { meetingCode, meetingType: 'EXHIBITION', aiApplicationType }),
        },
        {
          label: '用户日报',
          icon: 'Document',
          perm: ['user:admin:event:report:meeting:dailyDetail'],
          gate: 'exhibition',
          action: () => push('/daliyReport', { meetingCode, meetingType: aiApplicationType, meetingName }),
        },
        {
          label: '注册报表',
          icon: 'Histogram',
          perm: ['user:exhibition:exportRegisterReport'],
          action: () => downLoadReport('media'),
        },
        {
          label: '签到数据',
          icon: 'Finished',
          perm: ['user:exhibition:exportExSignUpReport'],
          gate: 'exhibition',
          action: () => downLoadReport('signUp'),
        },
        {
          label: '展会订单报表',
          icon: 'Tickets',
          perm: ['user:exhibition:exportExOrderReport'],
          gate: 'exhibition&!jbh',
          kind: 'export',
          action: () => downLoadReport('order'),
        },
        {
          label: '商机配对报表',
          icon: 'Connection',
          perm: ['user:exhibition:exportExOrderReport'],
          kind: 'export',
          action: () => downLoadReport('business'),
        },
      ],
    },
    {
      name: '管理',
      items: [
        { label: '预览', icon: 'View', perm: ['user:qrcode:getQrCodeUrls'], action: openPreview },
        {
          label: '注册管理',
          icon: 'List',
          perm: ['user:admin:attend:auditList'],
          action: () => push('/exhibitionManage/registerManage', { meetingCode }),
        },
        {
          label: '页面模版',
          icon: 'Files',
          perm: ['user:exhibition:forum'],
          action: () =>
            push('/exhibitionManage/exHIbitionPageConfig', { meetingCode, linkEnterpriseCode: enterpriseCode, logo }),
        },
        {
          label: '机构管理',
          icon: 'OfficeBuilding',
          perm: ['user:exhibitor:query'],
          action: () =>
            push('/exhibitionManage/brand', { meetingCode, enterpriseCode, meetingType: aiApplicationType }),
        },
        {
          label: '嘉宾管理',
          icon: 'User',
          gate: 'exhibition',
          action: () => push('/exhibitionManage/guestManage', { meetingCode }),
        },
        {
          label: '论坛关联',
          icon: 'ChatDotRound',
          perm: ['user:exhibition:forum'],
          action: () => push('/exhibitionManage/relatedForum', { meetingCode }),
        },
        {
          label: '供需匹配管理',
          icon: 'Share',
          perm: ['user:exhibition:forum'],
          gate: '!jbh',
          action: () => push('/exhibitionManage/matchingApply', { meetingCode }),
        },
        {
          label: '协同管理',
          icon: 'UserFilled',
          perm: ['TransferTeamwork'],
          action: () => holdMeetUserManageRef.value.handleOpen(rowObj()),
        },
        {
          label: '奖品/积分管理',
          icon: 'Present',
          gate: '!jbh',
          action: () => push('/marketing/lotteryManage', { meetingCode, type: 'EXHIBITION' }),
        },
        {
          label: '招聘机构管理',
          icon: 'Briefcase',
          gate: '!jbh',
          action: () =>
            push('/exhibitionManage/recruitmentBrand', { meetingCode, enterpriseCode, meetingType: aiApplicationType }),
        },
        { label: '议题论坛导出', icon: 'Download', kind: 'export', action: issueForumExportApi },
      ],
    },
    {
      name: '门票',
      gate: 'exhibition&!jbh',
      items: [
        {
          label: '门票配置',
          icon: 'Ticket',
          perm: ['user:ticket:getTicketCategory'],
          action: () => push('/exhibitionManage/ticketsConfig', { meetingCode }),
        },
        {
          label: '门票管理',
          icon: 'Tickets',
          perm: ['user:ticket:select'],
          action: () => push('/exhibitionManage/ticketsManage', { meetingCode }),
        },
        {
          label: '购票渠道',
          icon: 'ShoppingCart',
          perm: ['user:channel:page'],
          action: () => push('/exhibitionManage/channelManage', { meetingCode }),
        },
      ],
    },
    {
      name: '渠道',
      items: [
        {
          label: '主办渠道',
          icon: 'Share',
          perm: ['user:register:channel:list'],
          action: () =>
            push('/exhibitionManage/channelDefend', {
              meetingCode,
              navTitle: meetingName,
              pageId,
              logo,
              type: aiApplicationType,
            }),
        },
        {
          label: '分享渠道',
          icon: 'Share',
          perm: ['user:register:channel:list'],
          action: () =>
            push('/exhibitionManage/shareChannel', {
              meetingCode,
              navTitle: meetingName,
              pageId,
              logo,
              type: aiApplicationType,
            }),
        },
        {
          label: '邀请码渠道',
          icon: 'Message',
          perm: ['user:register:channel:list'],
          action: () => push('/exhibitionManage/shareCodeChannel', { meetingCode, meetingType: aiApplicationType }),
        },
        {
          label: '非报名渠道',
          icon: 'Connection',
          perm: ['user:register:channel:list'],
          action: () =>
            push('/exhibitionManage/channelUser', {
              meetingCode,
              navTitle: meetingName,
              pageId,
              logo,
              type: aiApplicationType,
            }),
        },
      ],
    },
  ]

  const tileGroups = computed(() =>
    allGroups
      .map((g) => ({ name: g.name, items: (g.items || []).filter(visible) }))
      .filter((g) => (!g.gate || checkGate(g.gate)) && g.items.length)
  )
</script>
